import React, { useEffect, useRef, useState, useMemo } from "react";
import { DoubleArrow } from "@mui/icons-material";
import * as Mui from "@mui/material";
import { Box, Typography, Paper, IconButton, Drawer } from "@mui/material";
import { CustomTextBox } from "components/CustomComponents/CustomTextBox";
import { useForm, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { themes } from "services/constants";
import { showToast } from "services/utils/Status";
import { CustomButton } from "components/CustomComponents/CustomButton";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import { onlyNumberRegex } from "services/constants/patterns";
import { useFetch } from "services/hooks/useFetch";

export const EditSalaryDetails = ({
  setOpenSidePeek, openSidePeek, editDetails, reloadData, setReloadData,
  importedData, setImportData, setButtonDisabled,
}) => {
  const {
    register, handleSubmit,
    formState: { errors },
    setValue, control, reset,
  } = useForm({
    defaultValues: {
      employee_id: "",
      user_name: "",
      company: "",
      template_name: "",
      salary: "",
      salary_type: "",
      effective_from: "",
      payroll_group: "",
    },
  });

  const employeeId    = useWatch({ control, name: "employee_id" });
  const userName      = useWatch({ control, name: "user_name" });
  const companyName   = useWatch({ control, name: "company" });
  const templateName  = useWatch({ control, name: "template_name" });
  const salary        = useWatch({ control, name: "salary" });
  const salaryType    = useWatch({ control, name: "salary_type" });
  const effectiveFrom = useWatch({ control, name: "effective_from" });
  const payrollGroup  = useWatch({ control, name: "payroll_group" });

  const { domain, token } = useSelector((state) => state.tokenReducer);
  const [errorMessage, setErrorMessage] = useState(editDetails?.error_messages || "");
  const initializedForId = useRef(null);

  // Local state for dynamic dependent dropdowns
  const [salaryTemplateData, setSalaryTemplateData] = useState([]);
  const [payrollGrpData, setPayrollGrpData] = useState([]);

  // Fetch Companies and Static Choices
  const { result: companyData } = useFetch(`${domain}company/`);
  const { result: componentCategoryData } = useFetch(`${domain}hrzone/salary/salary_template/get-salary-type-choices/`);

  // Extract the selected company's ID based on the company name
  const selectedCompanyId = useMemo(() => {
    if (!companyName || !companyData) return "";
    const matchedCompany = companyData.find(
      (c) => (c.companyname || c.name) === companyName
    );
    return matchedCompany ? matchedCompany.id : "";
  }, [companyName, companyData]);

  // Fetch dependent dropdowns whenever selectedCompanyId changes
  useEffect(() => {
    let isMounted = true;

    if (selectedCompanyId) {
      // Fetch Salary Templates based on company_id
      fetch(`${domain}hrzone/salary/salary_template/salary-template-list/?company_id=${selectedCompanyId}`, {
        method: "GET",
        headers: { "Content-Type": "application/json", Authorization: `token ${token}` },
      })
        .then((r) => r.json())
        .then((data) => {
          if (isMounted) {
            setSalaryTemplateData(Array.isArray(data) ? data : (data.results || data.data || []));
          }
        })
        .catch((e) => console.error("Error fetching salary templates", e));

      // Fetch Payroll Groups based on company ID
      fetch(`${domain}payroll/setup/payrollgroup/payroll-group/?company=${selectedCompanyId}`, {
        method: "GET",
        headers: { "Content-Type": "application/json", Authorization: `token ${token}` },
      })
        .then((r) => r.json())
        .then((data) => {
          if (isMounted) {
            setPayrollGrpData(Array.isArray(data) ? data : (data.results || data.data || []));
          }
        })
        .catch((e) => console.error("Error fetching payroll groups", e));
    } else {
      setSalaryTemplateData([]);
      setPayrollGrpData([]);
    }

    return () => { isMounted = false; };
  }, [selectedCompanyId, domain, token]);

  useEffect(() => {
    if (!openSidePeek) {
      initializedForId.current = null;
      return;
    }

    if (openSidePeek && editDetails && initializedForId.current !== editDetails.employee_id) {
      const fullRow = importedData?.find((r) => r.employee_id === editDetails.employee_id) || {};

      reset({
        employee_id:    fullRow.employee_id    || editDetails.employee_id    || "",
        user_name:      fullRow.user_name      || editDetails.user_name      || "",
        company:        fullRow.company        || editDetails.company        || "",
        template_name:  fullRow.template_name  || editDetails.template_name  || "",
        salary:         fullRow.salary         || editDetails.salary         || "",
        salary_type:    fullRow.salary_type    || editDetails.salary_type    || "",
        effective_from: fullRow.effective_from 
            ? fullRow.effective_from.split('-').reverse().join('-') 
            : editDetails.effective_from 
              ? editDetails.effective_from.split('-').reverse().join('-') 
              : "",
        payroll_group:  fullRow.payroll_group_name || editDetails.payroll_group_name || fullRow.payroll_group || "",
      });

      setErrorMessage(editDetails.error_messages || fullRow.error_messages || "");
      initializedForId.current = editDetails.employee_id;
      setReloadData(false);
    }
  }, [openSidePeek, editDetails, importedData, reset, setReloadData]);

  const handleUpdate = async (data) => {
    // We send data.payroll_group to BOTH payload fields because the validation API 
    // uses payroll_group_name to do the lookup.
    const payload = [{
      employee_id:    data.employee_id,
      user_name:      data.user_name,
      company:        data.company,
      template_name:  data.template_name,
      salary:         data.salary,
      salary_type:    data.salary_type,
      effective_from: data.effective_from.split('-').reverse().join('-'),
      payroll_group:  data.payroll_group,
      payroll_group_name: data.payroll_group, 
    }];

    try {
      const response = await fetch(`${domain}bulk-salary-import/`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `token ${token}` },
        body: JSON.stringify(payload),
      });

      const res = await response.json();

      if (response.ok) {
        // The validatedRow will contain payroll_group as an integer (if valid)
        // and error messages (if invalid).
        const validatedRow = res[0];
        setErrorMessage(validatedRow.error_messages || "");

        const updatedList = importedData.map((row) =>
          row.employee_id === editDetails.employee_id
            ? {
                ...row,
                company: data.company,
                template_name: data.template_name,
                salary_type: data.salary_type,
                effective_from: data.effective_from.split('-').reverse().join('-'),
                
                ...validatedRow, // CRITICAL: This allows the numeric ID to take over payroll_group
                
                // CRITICAL: Ensure the table UI text explicitly saves the string version of the dropdown 
                // so the user can still read the name in the data grid.
                payroll_group_name: data.payroll_group, 
                
                status: validatedRow.is_valid ? "Valid" : "Invalid",
              }
            : row
        );

        setImportData(updatedList);
        setButtonDisabled(updatedList.some((r) => !r.is_valid));

        if (validatedRow.is_valid) {
          showToast("success", "Salary details verified.");
          setOpenSidePeek(false);
        } else {
          showToast("error", validatedRow.error_messages || "Validation failed. Please correct errors.");
        }
      } else {
        const errText = res.errors ? JSON.stringify(res.errors) : "Update failed";
        setErrorMessage(errText);
      }
    } catch {
      showToast("error", "Network error occurred.");
    }
  };

  return (
    <Drawer anchor="right" open={openSidePeek} onClose={() => setOpenSidePeek(false)} PaperProps={{ sx: { width: 500 }, top: 64 }}>
      <Paper elevation={0} sx={{ borderBottom: `1px solid #eee`, borderRadius: 0 }}>
        <Box display="flex" alignItems="center" sx={{ padding: 1, backgroundColor: themes?.primary }}>
          <IconButton onClick={() => setOpenSidePeek(false)} sx={{ color: themes?.grayColor }}>
            <DoubleArrow />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 700, ml: 2 }}>Edit Salary Data</Typography>
        </Box>
      </Paper>

      <Box sx={{ flexGrow: 1, overflow: "auto", p: 2 }}>
        <form onSubmit={handleSubmit(handleUpdate)}>
          {errorMessage && (
            <Mui.Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 1.25, mb: 2, borderRadius: 1.5, backgroundColor: "rgba(239, 68, 68, 0.08)", border: "1px solid rgba(239, 68, 68, 0.3)" }}>
              <ErrorOutlineRoundedIcon sx={{ color: themes.backgroundRed, fontSize: 20 }} />
              <Mui.Typography variant="body2" sx={{ color: themes.backgroundRed, fontWeight: 500 }}>
                {errorMessage}
              </Mui.Typography>
            </Mui.Box>
          )}

          <Mui.Paper sx={{ p: 2 }}>
            <Mui.Grid container spacing={2}>

              <Mui.Grid item xs={12}>
                <Typography sx={{ fontWeight: 600 }}>Employee ID</Typography>
                <CustomTextBox 
                  disabled={true} 
                  selectedValue={employeeId || ""} 
                  updateDataFunction={(e) => setValue("employee_id", e.target.value)} 
                  rules={{ ...register("employee_id", { required: "Employee ID is required" }) }} 
                  errorMessage={errors?.employee_id?.message} 
                  textBoxError={!!errors.employee_id} 
                />
              </Mui.Grid>

              <Mui.Grid item xs={12}>
                <Typography sx={{ fontWeight: 600 }}>Employee Name</Typography>
                <CustomTextBox 
                  disabled={true} 
                  selectedValue={userName || ""} 
                  updateDataFunction={(e) => setValue("user_name", e.target.value)} 
                  rules={{ ...register("user_name", { required: "Employee Name is required" }) }} 
                  errorMessage={errors?.user_name?.message} 
                  textBoxError={!!errors.user_name} 
                />
              </Mui.Grid>

              <Mui.Grid item xs={12}>
                <Typography sx={{ fontWeight: 600 }}>Company</Typography>
                <Mui.TextField
                  select
                  fullWidth
                  size="small"
                  value={companyName || ""}
                  {...register("company", { required: "Company is required" })}
                  onChange={(e) => {
                    setValue("company", e.target.value, { shouldValidate: true });
                    setValue("template_name", "", { shouldValidate: true }); 
                    setValue("payroll_group", "", { shouldValidate: true }); 
                  }}
                  error={!!errors.company}
                  helperText={errors?.company?.message}
                >
                  {companyData?.map((option) => (
                    <Mui.MenuItem key={option.id} value={option.companyname || option.name}>
                      {option.companyname || option.name}
                    </Mui.MenuItem>
                  ))}
                </Mui.TextField>
              </Mui.Grid>

              <Mui.Grid item xs={12}>
                <Typography sx={{ fontWeight: 600 }}>Template Name</Typography>
                <Mui.TextField
                  select
                  fullWidth
                  size="small"
                  value={templateName || ""}
                  {...register("template_name", { required: "Template name is required" })}
                  onChange={(e) => setValue("template_name", e.target.value, { shouldValidate: true })}
                  error={!!errors.template_name}
                  helperText={errors?.template_name?.message}
                  disabled={!companyName}
                >
                  {salaryTemplateData?.map((option) => (
                    <Mui.MenuItem key={option.id || option.template_name} value={option.template_name}>
                      {option.template_name}
                    </Mui.MenuItem>
                  ))}
                </Mui.TextField>
              </Mui.Grid>

              <Mui.Grid item xs={12}>
                <Typography sx={{ fontWeight: 600 }}>Gross Salary</Typography>
                <CustomTextBox 
                  selectedValue={salary || ""} 
                  updateDataFunction={(e) => setValue("salary", e.target.value, { shouldValidate: true })} 
                  rules={{ ...register("salary", { required: "Salary is required", pattern: { value: onlyNumberRegex, message: "Only valid numbers allowed" } }) }} 
                  errorMessage={errors?.salary?.message} 
                  textBoxError={!!errors.salary} 
                />
              </Mui.Grid>

              <Mui.Grid item xs={12}>
                <Typography sx={{ fontWeight: 600 }}>Salary Type</Typography>
                <Mui.TextField
                  select
                  fullWidth
                  size="small"
                  value={salaryType || ""}
                  {...register("salary_type", { required: "Salary type is required" })}
                  onChange={(e) => setValue("salary_type", e.target.value, { shouldValidate: true })}
                  error={!!errors.salary_type}
                  helperText={errors?.salary_type?.message}
                >
                  {componentCategoryData?.map((option, idx) => {
                    const displayValue = option?.label || option;
                    const val = option?.value || option?.label || option;
                    return (
                      <Mui.MenuItem key={idx} value={val}>
                        {displayValue}
                      </Mui.MenuItem>
                    );
                  })}
                </Mui.TextField>
              </Mui.Grid>

              <Mui.Grid item xs={12}>
                <Typography sx={{ fontWeight: 600 }}>Effective From (DD-MM-YYYY)</Typography>
                <CustomTextBox 
                  selectedValue={effectiveFrom || ""} 
                  updateDataFunction={(e) => setValue("effective_from", e.target.value, { shouldValidate: true })} 
                  rules={{ ...register("effective_from", { required: "Effective From date is required", pattern: { value: /^(0[1-9]|[12][0-9]|3[01])\-(0[1-9]|1[012])\-\d{4}$/, message: "Date format must be DD-MM-YYYY" } }) }} 
                  errorMessage={errors?.effective_from?.message} 
                  textBoxError={!!errors.effective_from} 
                />
              </Mui.Grid>

              <Mui.Grid item xs={12}>
                <Typography sx={{ fontWeight: 600 }}>Payroll Group</Typography>
                <Mui.TextField
                  select
                  fullWidth
                  size="small"
                  value={payrollGroup || ""}
                  {...register("payroll_group", { required: "Payroll group is required" })}
                  onChange={(e) => setValue("payroll_group", e.target.value, { shouldValidate: true })}
                  error={!!errors.payroll_group}
                  helperText={errors?.payroll_group?.message}
                  disabled={!companyName}
                >
                  {payrollGrpData?.map((option) => (
                    <Mui.MenuItem key={option.id} value={option.name}>
                      {option.name}
                    </Mui.MenuItem>
                  ))}
                </Mui.TextField>
              </Mui.Grid>

            </Mui.Grid>
          </Mui.Paper>

          <Mui.Grid container sx={{ mt: 4, justifyContent: "space-between" }}>
            <CustomButton actionFuntion={() => setOpenSidePeek(false)} actionName="Cancel" typeName="button" />
            <CustomButton actionName="Re-Verify" typeName="submit" />
          </Mui.Grid>
        </form>
      </Box>
    </Drawer>
  );
};